import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  LabelValue,
  LabelValueSource,
  WbsLabelValueSearchResult
} from 'graphql/types';

import { useLabelsFormFieldContext } from 'modules/timesheet-labels';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import { useFeatureFlag } from 'modules/common/hooks';
import { useIsSuggestion } from 'modules/common/hooks/useIsSuggestion';
import { DependantLabels } from '../schema';
import {
  determineLabelAcceptedBy,
  usePCPTAutoUpdateHandlers
} from '../../hooks';

export const useClientOnSelect = (
  onSelect: (value: LabelValue | null) => void,
  dependentLabels: DependantLabels
): ((value?: WbsLabelValueSearchResult | null) => void) => {
  const { setValue } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const {
    project: autoUpdateProject,
    task: autoUpdateTask
  } = usePCPTAutoUpdateHandlers({
    dependentLabels
  });

  const isSuggestion = useIsSuggestion(dependentLabels.project.label);
  const onClientSelect = useCallback(
    async (value?: WbsLabelValueSearchResult | null) => {
      const transformedValue = {
        idValue: value?.id,
        numericValue: null,
        stringValue: value?.displayText
      };

      const { client, project, task, billing } = dependentLabels;
      const valueSourceOnReset = isSmartSuggestionEnabled
        ? LabelValueSource.Suggestion
        : LabelValueSource.User;

      const labelValue = isSuggestion
        ? client?.label?.suggestedValues?.[0]
        : client?.label?.value;
      const isClientDifferent = labelValue?.idValue !== value?.id;
      if (isClientDifferent) {
        setValue(
          `${timesheetLabelsFormField}.${project.index}.label`,
          {
            ...project.label,
            value: null,
            ...(isTimeSignalFederatedSuggestionsEnabled
              ? {
                  suggestedValues: null
                }
              : {}),
            valueSource: valueSourceOnReset,
            acceptedBy: determineLabelAcceptedBy(
              valueSourceOnReset,
              null,
              isSmartSuggestionEnabled
            )
          },
          { shouldValidate: true, shouldDirty: true }
        );

        setValue(
          `${timesheetLabelsFormField}.${task.index}.label`,
          {
            ...task.label,
            value: null,
            ...(isTimeSignalFederatedSuggestionsEnabled
              ? {
                  suggestedValues: null
                }
              : {}),
            valueSource: valueSourceOnReset,
            acceptedBy: determineLabelAcceptedBy(
              valueSourceOnReset,
              null,
              isSmartSuggestionEnabled
            )
          },
          { shouldValidate: true, shouldDirty: true }
        );

        if (billing && billing.label) {
          setValue(
            `${timesheetLabelsFormField}.${billing.index}.label`,
            {
              ...billing.label,
              value: null,
              ...(isTimeSignalFederatedSuggestionsEnabled
                ? {
                    suggestedValues: null
                  }
                : {}),
              valueSource: valueSourceOnReset,
              acceptedBy: determineLabelAcceptedBy(
                valueSourceOnReset,
                null,
                isSmartSuggestionEnabled
              )
            },
            { shouldValidate: true, shouldDirty: true }
          );
        }

        if (value) {
          const projectResult = await autoUpdateProject({ client: value?.id });

          if (projectResult) {
            await autoUpdateTask({
              client: value?.id,
              project: projectResult.id
            });
          }
        }
      }
      onSelect(value ? transformedValue : null);
    },
    [
      dependentLabels,
      isSmartSuggestionEnabled,
      isSuggestion,
      onSelect,
      setValue,
      timesheetLabelsFormField,
      isTimeSignalFederatedSuggestionsEnabled,
      autoUpdateProject,
      autoUpdateTask
    ]
  );

  return onClientSelect;
};

export default useClientOnSelect;
